import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, Plus, X, Check } from 'lucide-react';
import { cn, Avatar, Badge, Spinner } from '@mellify/ui';
import { useWorkspaces } from '../../hooks/useWorkspaces';
import { useWorkspaceSwitcher } from '../../hooks/useWorkspaceSwitcher';
import { useWorkspaceStore } from '../../stores/workspaceStore';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../routes/routeConfig';
import type { WorkspaceListItem } from '../../services/workspaceService';
import type { SubscriptionPlan } from '@mellify/shared';

const planLabels: Record<SubscriptionPlan, string> = {
  free: 'Free',
  starter: 'Starter',
  professional: 'Pro',
  enterprise: 'Enterprise',
};

const planVariants: Record<SubscriptionPlan, 'dark' | 'primary' | 'info' | 'warning'> = {
  free: 'dark',
  starter: 'primary',
  professional: 'info',
  enterprise: 'warning',
};

export interface WorkspaceSwitcherModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
}

export const WorkspaceSwitcherModal: React.FC<WorkspaceSwitcherModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: workspaces, isLoading } = useWorkspaces();
  const { switchWorkspace } = useWorkspaceSwitcher();
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);

  // Filter workspaces based on search
  const filteredWorkspaces = useMemo(() => {
    if (!workspaces) return [];
    if (!searchQuery.trim()) return workspaces;

    const query = searchQuery.toLowerCase().trim();
    return workspaces.filter(
      (ws) =>
        ws.name.toLowerCase().includes(query) ||
        ws.slug.toLowerCase().includes(query) ||
        (ws.industry && ws.industry.toLowerCase().includes(query))
    );
  }, [workspaces, searchQuery]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSelectedIndex(0);
      // Focus input after animation
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen]);

  // Clamp selected index
  useEffect(() => {
    if (selectedIndex >= filteredWorkspaces.length) {
      setSelectedIndex(Math.max(0, filteredWorkspaces.length - 1));
    }
  }, [filteredWorkspaces.length, selectedIndex]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const selectedEl = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
    selectedEl?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const handleSelect = useCallback(
    async (workspace: WorkspaceListItem) => {
      if (workspace.id === currentWorkspace?.id) {
        onClose();
        return;
      }
      await switchWorkspace(workspace);
      onClose();
    },
    [switchWorkspace, currentWorkspace, onClose]
  );

  const handleCreateNew = useCallback(() => {
    onClose();
    navigate(ROUTES.DASHBOARD_WORKSPACES);
  }, [onClose, navigate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Total items = filtered workspaces + 1 (create new)
      const totalItems = filteredWorkspaces.length + 1;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % totalItems);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + totalItems) % totalItems);
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex < filteredWorkspaces.length) {
            handleSelect(filteredWorkspaces[selectedIndex]!);
          } else {
            handleCreateNew();
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [filteredWorkspaces, selectedIndex, handleSelect, handleCreateNew, onClose]
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-modal bg-dark-900/50 dark:bg-black/60 animate-overlay-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-modal flex items-start justify-center pt-[15vh] px-4">
        <div
          className="w-full max-w-lg bg-white dark:bg-dark-800 rounded-xl shadow-modal border border-dark-200 dark:border-dark-700 overflow-hidden animate-modal-in"
          role="dialog"
          aria-modal="true"
          aria-label="Switch workspace"
          onKeyDown={handleKeyDown}
        >
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-dark-200 dark:border-dark-700">
            <Search className="w-5 h-5 text-dark-400 dark:text-dark-500 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search workspaces..."
              className="flex-1 bg-transparent text-sm text-dark-900 dark:text-dark-100 placeholder:text-dark-400 dark:placeholder:text-dark-500 outline-none"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-md text-dark-400 hover:text-dark-600 dark:text-dark-500 dark:hover:text-dark-300 hover:bg-dark-100 dark:hover:bg-dark-700 transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Keyboard hint */}
          <div className="px-4 py-1.5 border-b border-dark-100 dark:border-dark-700/50 bg-dark-50 dark:bg-dark-800/50">
            <p className="text-xs text-dark-400 dark:text-dark-500">
              <kbd className="px-1 py-0.5 rounded bg-dark-200 dark:bg-dark-700 text-dark-600 dark:text-dark-300 font-mono text-[10px]">↑↓</kbd>{' '}
              navigate{' '}
              <kbd className="px-1 py-0.5 rounded bg-dark-200 dark:bg-dark-700 text-dark-600 dark:text-dark-300 font-mono text-[10px]">↵</kbd>{' '}
              select{' '}
              <kbd className="px-1 py-0.5 rounded bg-dark-200 dark:bg-dark-700 text-dark-600 dark:text-dark-300 font-mono text-[10px]">esc</kbd>{' '}
              close
            </p>
          </div>

          {/* Workspace list */}
          <div
            ref={listRef}
            className="max-h-[320px] overflow-y-auto scrollbar-thin py-1"
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="md" variant="primary" />
              </div>
            ) : filteredWorkspaces.length === 0 && searchQuery ? (
              <div className="py-8 text-center">
                <p className="text-sm text-dark-500 dark:text-dark-400">
                  No workspaces matching "{searchQuery}"
                </p>
              </div>
            ) : (
              <>
                {filteredWorkspaces.map((workspace, index) => {
                  const isActive = currentWorkspace?.id === workspace.id;
                  const isSelected = selectedIndex === index;

                  return (
                    <button
                      key={workspace.id}
                      type="button"
                      data-index={index}
                      onClick={() => handleSelect(workspace)}
                      className={cn(
                        'flex items-center gap-3 w-full px-4 py-2.5 text-left transition-colors outline-none',
                        isSelected
                          ? 'bg-primary-50 dark:bg-primary-900/20'
                          : 'hover:bg-dark-50 dark:hover:bg-dark-700/50',
                        isActive && 'bg-primary-50/50 dark:bg-primary-900/10'
                      )}
                    >
                      <Avatar
                        src={workspace.logoUrl}
                        name={workspace.name}
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-dark-900 dark:text-dark-100 truncate">
                            {workspace.name}
                          </span>
                          {isActive && (
                            <Check className="w-4 h-4 text-primary-500 shrink-0" />
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge
                            variant={planVariants[workspace.subscriptionPlan]}
                            size="sm"
                          >
                            {planLabels[workspace.subscriptionPlan]}
                          </Badge>
                          <span className="text-xs text-dark-400 dark:text-dark-500">
                            {workspace.memberCount} {workspace.memberCount === 1 ? 'member' : 'members'}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}

                {/* Create New Workspace */}
                <button
                  type="button"
                  data-index={filteredWorkspaces.length}
                  onClick={handleCreateNew}
                  className={cn(
                    'flex items-center gap-3 w-full px-4 py-2.5 text-left transition-colors outline-none border-t border-dark-100 dark:border-dark-700/50 mt-1',
                    selectedIndex === filteredWorkspaces.length
                      ? 'bg-primary-50 dark:bg-primary-900/20'
                      : 'hover:bg-dark-50 dark:hover:bg-dark-700/50'
                  )}
                >
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-dark-100 dark:bg-dark-700 shrink-0">
                    <Plus className="w-4 h-4 text-dark-500 dark:text-dark-400" />
                  </div>
                  <span className="text-sm font-medium text-dark-700 dark:text-dark-300">
                    Create New Workspace
                  </span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

WorkspaceSwitcherModal.displayName = 'WorkspaceSwitcherModal';